import React from "react";
import { ArrowRight, CheckCircle2, ShieldCheck, WifiOff } from "lucide-react";
import heroImg from "../assets/img/kntopass-hero.jpg";
import { CONTACT, STATS } from "../../data/content";

const Hero = () => (
  <section id="top" className="relative isolate overflow-hidden pt-32 sm:pt-40">
    {/* Background: grid, brand glow, fade */}
    <div className="absolute inset-0 -z-10 bg-grid bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" />
    <div className="absolute left-1/2 top-[-18rem] -z-10 h-[40rem] w-[64rem] -translate-x-1/2 rounded-full bg-brand-600/25 blur-[140px]" />
    <div className="absolute right-[-10rem] top-40 -z-10 h-[28rem] w-[28rem] rounded-full bg-knto-600/20 blur-[120px]" />

    <div className="container">
      <div className="mx-auto max-w-4xl text-center">
        <a
          href="#platform"
          className="eyebrow group normal-case tracking-normal transition hover:border-white/20 hover:text-white"
        >
          <span className="rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
            New
          </span>
          The makers of KNTO Pass
          <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
        </a>

        <h1 className="mt-8 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
          One platform for <span className="text-accent">every access point</span>
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-ink-300 sm:text-xl">
          Fronix Studio builds secure access infrastructure: cloud software, mobile apps and readers
          made in Egypt, working together as one system for communities, events, gyms and workplaces.
        </p>

        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a href={CONTACT.kntoUrl} target="_blank" rel="noopener noreferrer" className="btn-primary btn-lg w-full sm:w-auto">
            Explore KNTO Pass <ArrowRight className="h-4 w-4" />
          </a>
          <a href="#contact" className="btn-ghost btn-lg w-full sm:w-auto">
            Book a demo
          </a>
        </div>

        <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-ink-400">
          {["QR, NFC, RFID & BLE", "Works offline", "English & Arabic"].map((t) => (
            <li key={t} className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-brand-500" /> {t}
            </li>
          ))}
        </ul>
      </div>

      {/* Product visual */}
      <div className="relative mx-auto mt-16 max-w-6xl sm:mt-20">
        <div className="absolute inset-x-10 -top-6 bottom-10 -z-10 rounded-[2.5rem] bg-gradient-to-b from-brand-600/30 to-transparent blur-2xl" />
        <div className="rounded-[1.75rem] border border-white/10 bg-white/[0.04] p-1.5 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] backdrop-blur sm:rounded-[2rem] sm:p-2">
          <img
            src={heroImg}
            alt="KNTO Pass web portal showing a residential project with live access logs, beside the mobile app with a dynamic QR access card"
            width="2400"
            height="1350"
            fetchpriority="high"
            className="w-full rounded-[1.4rem] sm:rounded-[1.6rem]"
          />
        </div>

        {/* Floating chips */}
        <div className="absolute -left-6 bottom-[14%] hidden animate-float items-center gap-3 rounded-2xl border border-white/10 bg-ink-900/90 px-4 py-3 shadow-xl backdrop-blur md:flex">
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
            <span className="absolute inset-0 animate-pulse-ring rounded-full bg-emerald-500/30" />
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div className="text-left">
            <p className="text-sm font-semibold text-white">Access granted</p>
            <p className="text-xs text-ink-400">Main gate · just now</p>
          </div>
        </div>
        <div className="absolute -right-6 top-[8%] hidden animate-float-slow items-center gap-3 rounded-2xl border border-white/10 bg-ink-900/90 px-4 py-3 shadow-xl backdrop-blur md:flex">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-600/15 text-brand-400">
            <WifiOff className="h-5 w-5" />
          </span>
          <div className="text-left">
            <p className="text-sm font-semibold text-white">Offline validation</p>
            <p className="text-xs text-ink-400">Gates keep working</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <dl className="mx-auto mt-20 grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 lg:grid-cols-4">
        {STATS.map((s) => (
          <div key={s.label} className="flex flex-col-reverse bg-ink-950 px-6 py-8 text-center">
            <dt className="mt-2 text-sm text-ink-400">{s.label}</dt>
            <dd className="font-display text-4xl font-semibold text-white">{s.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  </section>
);

export default Hero;
